/**
 * @fileoverview Email, birth date, gender, and area fields for one additional contact.
 */

import React from "react";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import { IconButton } from "@mui/material";
import Select from "react-select";
import { ReactComponent as MailIcon } from "../../../assets/icons/ContactInformation/MailIcon.svg";
import { getSelectedOption, selectStyles } from "../../../utils/common";
import { CONTACT_INFORMATION_I18N } from "./additionalContactConstants";
import styles from "./ContactsData.module.css";

const AdditionalContactEmailField = ({
  t,
  index,
  contact,
  form,
  getFieldValidationState,
  getErrorMessage,
}) => {
  const {
    showMainEmailAddress,
    setShowMainEmailAddress,
    handleInputChange,
    handleToggleChange,
  } = form;

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactsData.emailAddressLabel")}
      </label>
      <div className={styles.inputWrapper}>
        <MailIcon className={styles.inputIcon} />
        <input
          type="email"
          className={`${styles.input} ${styles.inputWithIcon} ${
            getFieldValidationState(
              index,
              "correoElectronico",
              contact?.correoElectronico || "",
            ) === "error"
              ? styles.inputError
              : ""
          }`}
          placeholder={t("components.contactsData.emailAddressPlaceholder")}
          value={contact?.correoElectronico || ""}
          onChange={(e) =>
            handleInputChange("correoElectronico", e.target.value)
          }
        />
      </div>
      {getErrorMessage(
        index,
        "correoElectronico",
        contact?.correoElectronico || "",
      ) && (
        <p className={styles.errorMessage}>
          {getErrorMessage(
            index,
            "correoElectronico",
            contact?.correoElectronico || "",
          )}
        </p>
      )}
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(CONTACT_INFORMATION_I18N.showInProfile)}
        </span>
        <IconButton
          onClick={() => {
            setShowMainEmailAddress(!showMainEmailAddress);
            handleToggleChange(
              "mostrarCorreoElectronico",
              !showMainEmailAddress,
            );
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: showMainEmailAddress ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {showMainEmailAddress ? (
            <ToggleOnIcon color="primary" />
          ) : (
            <ToggleOffIcon />
          )}
        </IconButton>
      </div>
    </div>
  );
};

const AdditionalContactBirthDateField = ({
  t,
  index,
  form,
  getFieldValidationState,
  getErrorMessage,
}) => {
  const {
    birthDate,
    setBirthDate,
    showBirthDate,
    setShowBirthDate,
    handleInputChange,
    handleToggleChange,
  } = form;

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactsData.dateOfBirthLabel")}
      </label>
      <div className={styles.inputWrapper}>
        <input
          type="date"
          placeholder={t("common.dateFormatPlaceholder")}
          className={`${styles.input} ${
            getFieldValidationState(index, "fechaNacimiento", birthDate) ===
            "error"
              ? styles.inputError
              : ""
          }`}
          value={birthDate}
          onChange={(e) => {
            setBirthDate(e.target.value);
            handleInputChange("fechaNacimiento", e.target.value);
          }}
        />
      </div>
      {getErrorMessage(index, "fechaNacimiento", birthDate) && (
        <p className={styles.errorMessage}>
          {getErrorMessage(index, "fechaNacimiento", birthDate)}
        </p>
      )}
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(CONTACT_INFORMATION_I18N.showInProfile)}
        </span>
        <IconButton
          onClick={() => {
            setShowBirthDate(!showBirthDate);
            handleToggleChange("mostrarFechaNacimiento", !showBirthDate);
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: showBirthDate ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {showBirthDate ? (
            <ToggleOnIcon color="primary" />
          ) : (
            <ToggleOffIcon />
          )}
        </IconButton>
      </div>
    </div>
  );
};

const AdditionalContactGenderField = ({
  t,
  genderOptions,
  form,
}) => {
  const {
    selectedGender,
    setSelectedGender,
    showGender,
    setShowGender,
    handleInputChange,
    handleToggleChange,
  } = form;

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactsData.genderLabel")}
      </label>
      <Select
        value={getSelectedOption(genderOptions, selectedGender)}
        onChange={(selectedOption) => {
          setSelectedGender(selectedOption.value);
          handleInputChange("genero", selectedOption.value);
        }}
        options={genderOptions}
        placeholder={t("components.contactsData.genderPlaceholder")}
        styles={selectStyles}
        className="select__control"
      />
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(CONTACT_INFORMATION_I18N.showInProfile)}
        </span>
        <IconButton
          onClick={() => {
            setShowGender(!showGender);
            handleToggleChange("mostrarGenero", !showGender);
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: showGender ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {showGender ? <ToggleOnIcon color="primary" /> : <ToggleOffIcon />}
        </IconButton>
      </div>
    </div>
  );
};

const AdditionalContactAreaField = ({ t, areaOptions, form }) => {
  const {
    selectedArea,
    setSelectedArea,
    showArea,
    setShowArea,
    handleInputChange,
  } = form;

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactsData.areaLabel")}
      </label>
      <Select
        value={getSelectedOption(areaOptions, selectedArea)}
        options={areaOptions}
        placeholder={t("components.contactsData.areaPlaceholder")}
        styles={selectStyles}
        className="select__control"
        onChange={(selectedOption) => {
          setSelectedArea(selectedOption.value);
          handleInputChange("area", selectedOption.value);
        }}
      />
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(CONTACT_INFORMATION_I18N.showInProfile)}
        </span>
        <IconButton
          onClick={() => {
            setShowArea(!showArea);
            handleInputChange("mostrarArea", !showArea);
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: showArea ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {showArea ? <ToggleOnIcon /> : <ToggleOffIcon />}
        </IconButton>
      </div>
    </div>
  );
};

export const AdditionalContactProfileFields = ({
  t,
  index,
  contact,
  genderOptions,
  areaOptions,
  form,
  getFieldValidationState,
  getErrorMessage,
}) => (
  <div className={styles.inputRow}>
    <AdditionalContactEmailField
      t={t}
      index={index}
      contact={contact}
      form={form}
      getFieldValidationState={getFieldValidationState}
      getErrorMessage={getErrorMessage}
    />
    <AdditionalContactBirthDateField
      t={t}
      index={index}
      form={form}
      getFieldValidationState={getFieldValidationState}
      getErrorMessage={getErrorMessage}
    />
    <AdditionalContactGenderField
      t={t}
      genderOptions={genderOptions}
      form={form}
    />
    <AdditionalContactAreaField t={t} areaOptions={areaOptions} form={form} />
  </div>
);
